import { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { formatError } from "../utils/formatError";
import { fromDoc } from "../utils/firestoreDoc";
import logger from "../utils/logger";
import type { Notification } from "../types";

const NOTIFICATION_LIMIT = 100;

export function useNotifications(userId: string | null | undefined) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    setError(null);
    const q = query(
      collection(db, "notifications"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(NOTIFICATION_LIMIT)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setNotifications(snapshot.docs.map((d) => fromDoc<Notification>(d)));
    }, (err) => {
      logger.error("Notifications listener error:", err);
      setError(formatError(err));
    });
    return () => unsubscribe();
  }, [userId]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markRead = async (notificationId: string) => {
    try {
      await updateDoc(doc(db, "notifications", notificationId), { read: true });
    } catch (err) {
      throw new Error(formatError(err));
    }
  };

  const markAllRead = async () => {
    try {
      const batch = writeBatch(db);
      notifications
        .filter((n) => !n.read)
        .forEach((n) => {
          batch.update(doc(db, "notifications", n.id), { read: true });
        });
      await batch.commit();
    } catch (err) {
      throw new Error(formatError(err));
    }
  };

  const notify = async (targetUserId: string, type: string, title: string, message: string, jobId: string | null, agencyId: string | null) => {
    try {
      await addDoc(collection(db, "notifications"), {
        userId: targetUserId,
        type,
        title,
        message,
        jobId: jobId || null,
        agencyId: agencyId || null,
        read: false,
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      throw new Error(formatError(err));
    }
  };

  const notifyMultiple = async (userIds: string[], type: string, title: string, message: string, jobId: string | null, agencyId: string | null) => {
    try {
      const batch = writeBatch(db);
      userIds.forEach((uid) => {
        const ref = doc(collection(db, "notifications"));
        batch.set(ref, {
          userId: uid,
          type,
          title,
          message,
          jobId: jobId || null,
          agencyId: agencyId || null,
          read: false,
          createdAt: serverTimestamp(),
        });
      });
      await batch.commit();
    } catch (err) {
      throw new Error(formatError(err));
    }
  };

  return { notifications, unreadCount, error, markRead, markAllRead, notify, notifyMultiple };
}
